import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Send,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  History,
  Edit,
  Trash2,
  Loader2,
  Calendar,
  Building2,
  RefreshCw,
  Filter
} from "lucide-react";


// Status configuration with colors and labels
const STATUS_CONFIG = {
  draft: {
    color: "bg-slate-100 text-slate-700 border-slate-300",
    icon: FileText,
    cs: "Koncept",
    en: "Draft"
  },
  submitted: {
    color: "bg-blue-100 text-blue-700 border-blue-300",
    icon: Send,
    cs: "Podáno",
    en: "Submitted"
  },
  pending_supplement: {
    color: "bg-orange-100 text-orange-700 border-orange-300",
    icon: AlertTriangle,
    cs: "Čeká na doplnění",
    en: "Pending Supplement"
  },
  approved: {
    color: "bg-green-100 text-green-700 border-green-300",
    icon: CheckCircle,
    cs: "Schváleno",
    en: "Approved"
  },
  rejected: {
    color: "bg-red-100 text-red-700 border-red-300",
    icon: XCircle,
    cs: "Zamítnuto",
    en: "Rejected"
  },
  expired: {
    color: "bg-yellow-100 text-yellow-700 border-yellow-300",
    icon: Clock,
    cs: "Vypršelo",
    en: "Expired"
  }
};

const SubmissionsPage = () => {
  const { getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const { registerScopeChangeGuard } = useOrganizationScope();

  // State
  const [submissions, setSubmissions] = useState([]);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    pending_supplement_count: 0,
    expired_count: 0
  });
  
  // Form state
  const [formData, setFormData] = useState({
    mission_id: "",
    status: "draft",
    notes: "",
    expires_at: ""
  });
  
  // Status update state
  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    note: ""
  });
  
  // Translations
  const t = language === "cs" ? {
    title: "Sledování podání ÚCL",
    subtitle: "Správa a sledování stavu podání na Úřad civilního letectví",
    newSubmission: "Nové podání",
    editSubmission: "Upravit podání",
    noSubmissions: "Žádná podání",
    noSubmissionsDesc: "Zatím nemáte žádná podání k ÚCL.",
    mission: "Mise",
    status: "Stav",
    submittedAt: "Datum podání",
    updatedAt: "Poslední změna",
    expiresAt: "Platnost do",
    notes: "Poznámky / Požadavky ÚCL",
    actions: "Akce",
    save: "Uložit",
    cancel: "Zrušit",
    delete: "Smazat",
    deleteConfirmTitle: "Smazat podání?",
    deleteConfirmDesc: "Tato akce je nevratná. Podání bude trvale odstraněno.",
    selectMission: "Vyberte misi",
    selectStatus: "Vyberte stav",
    viewDetail: "Detail",
    updateStatus: "Změnit stav",
    statusHistory: "Historie stavů",
    noHistory: "Zatím žádné změny stavu",
    statusNote: "Poznámka ke změně",
    statusNotePlaceholder: "Např. požadavek ÚCL na doplnění dokumentace...",
    warningPendingSupplement: "podání čeká na doplnění",
    warningExpired: "podání vypršelo",
    filterAll: "Všechny",
    changedBy: "Změnil",
    created: "Vytvořeno",
    loading: "Načítání...",
    saving: "Ukládání..."
  } : {
    title: "UCL Submission Tracker",
    subtitle: "Manage and track CAA submission status",
    newSubmission: "New Submission",
    editSubmission: "Edit Submission",
    noSubmissions: "No Submissions",
    noSubmissionsDesc: "You don't have any CAA submissions yet.",
    mission: "Mission",
    status: "Status",
    submittedAt: "Submitted At",
    updatedAt: "Last Updated",
    expiresAt: "Expires At",
    notes: "Notes / CAA Requests",
    actions: "Actions",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    deleteConfirmTitle: "Delete Submission?",
    deleteConfirmDesc: "This action cannot be undone. The submission will be permanently deleted.",
    selectMission: "Select mission",
    selectStatus: "Select status",
    viewDetail: "Details",
    updateStatus: "Update Status",
    statusHistory: "Status History",
    noHistory: "No status changes yet",
    statusNote: "Status Note",
    statusNotePlaceholder: "E.g., CAA request for additional documentation...",
    warningPendingSupplement: "submission(s) pending supplement",
    warningExpired: "submission(s) expired",
    filterAll: "All",
    changedBy: "Changed by",
    created: "Created",
    loading: "Loading...",
    saving: "Saving..."
  };
  
  // Fetch data
  const fetchSubmissions = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await api.get(`/submissions${params}`, {
        headers: getAuthHeaders()
      });
      setSubmissions(response.data.submissions || []);
      setStats({
        pending_supplement_count: response.data.pending_supplement_count || 0,
        expired_count: response.data.expired_count || 0
      });
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
      toast.error(language === "cs" ? "Nepodařilo se načíst podání" : "Failed to load submissions");
    }
  }, [statusFilter, language]);
  
  const fetchMissions = useCallback(async () => {
    try {
      const response = await api.get(`/missions`, {
        headers: getAuthHeaders()
      });
      setMissions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch missions:", error);
    }
  }, [getAuthHeaders]);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSubmissions(), fetchMissions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSubmissions, fetchMissions]);

  useEffect(() => {
    if (!showModal) return undefined;
    return registerScopeChangeGuard(() => window.confirm(
      language === 'cs'
        ? 'Rozpracovaný formulář se zavřením zahodí. Opravdu chcete přepnout organizaci?'
        : 'Closing this form will discard your unsaved changes. Switch organization anyway?'
    ));
  }, [showModal, registerScopeChangeGuard, language]);

  // Handlers
  const handleOpenNew = () => {
    setSelectedSubmission(null);
    setFormData({
      mission_id: "",
      status: "draft",
      notes: "",
      expires_at: ""
    });
    setShowModal(true);
  };
  
  const handleEdit = (submission) => {
    setSelectedSubmission(submission);
    setFormData({
      mission_id: submission.mission_id,
      status: submission.status,
      notes: submission.notes || "",
      expires_at: submission.expires_at ? submission.expires_at.split("T")[0] : ""
    });
    setShowModal(true);
  };
  
  const handleViewDetail = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };
  
  const handleOpenStatusUpdate = (submission) => {
    setSelectedSubmission(submission);
    setStatusUpdate({
      status: submission.status,
      note: ""
    });
    setShowStatusModal(true);
  };
  
  const handleSave = async () => {
    if (!formData.mission_id) {
      toast.error(language === "cs" ? "Vyberte misi" : "Select a mission");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        mission_id: formData.mission_id,
        status: formData.status,
        notes: formData.notes || null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };
      
      if (selectedSubmission) {
        // Update existing
        await api.patch(`/submissions/${selectedSubmission.id}`, {
          notes: payload.notes,
          expires_at: payload.expires_at
        }, {
          headers: getAuthHeaders()
        });
        toast.success(language === "cs" ? "Podání aktualizováno" : "Submission updated");
      } else {
        // Create new
        await api.post(`/submissions`, payload, {
          headers: getAuthHeaders()
        });
        toast.success(language === "cs" ? "Podání vytvořeno" : "Submission created");
      }
      
      setShowModal(false);
      fetchSubmissions();
    } catch (error) {
      console.error("Failed to save submission:", error);
      toast.error(error.response?.data?.detail || (language === "cs" ? "Nepodařilo se uložit" : "Failed to save"));
    } finally {
      setSaving(false);
    }
  };
  
  const handleStatusUpdate = async () => {
    if (!statusUpdate.status) return;
    
    setSaving(true);
    try {
      await api.patch(`/submissions/${selectedSubmission.id}/status`, {
        status: statusUpdate.status,
        note: statusUpdate.note || null
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(language === "cs" ? "Stav aktualizován" : "Status updated");
      setShowStatusModal(false);
      fetchSubmissions();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error(error.response?.data?.detail || (language === "cs" ? "Nepodařilo se aktualizovat stav" : "Failed to update status"));
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/submissions/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success(language === "cs" ? "Podání smazáno" : "Submission deleted");
      setDeleteConfirm(null);
      fetchSubmissions();
    } catch (error) {
      console.error("Failed to delete submission:", error);
      toast.error(error.response?.data?.detail || (language === "cs" ? "Nepodařilo se smazat" : "Failed to delete"));
    }
  };
  
  // Status badge component
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    const label = language === "cs" ? config.cs : config.en;
    
    return (
      <Badge variant="outline" className={`${config.color} gap-1.5 font-medium`}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </Badge>
    );
  };
  
  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString(language === "cs" ? "cs-CZ" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="submissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t.title}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{t.subtitle}</p>
        </div>
        <Button 
          onClick={handleOpenNew}
          className="gap-2 bg-[#1E3A5F] hover:bg-[#2a4a73]"
          data-testid="new-submission-btn"
        >
          <Plus className="h-4 w-4" />
          {t.newSubmission}
        </Button>
      </div>
      
      {/* Warning Cards */}
      {(stats.pending_supplement_count > 0 || stats.expired_count > 0) && (
        <div className="flex gap-4">
          {stats.pending_supplement_count > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="flex items-center gap-3 py-3">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <span className="text-orange-700 font-medium">
                  {stats.pending_supplement_count} {t.warningPendingSupplement}
                </span>
              </CardContent>
            </Card>
          )}
          {stats.expired_count > 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="flex items-center gap-3 py-3">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="text-yellow-700 font-medium">
                  {stats.expired_count} {t.warningExpired}
                </span>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-[var(--text-muted)]" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.filterAll}</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {language === "cs" ? config.cs : config.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={fetchSubmissions} className="gap-1.5">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Submissions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            {t.title}
          </CardTitle>
          <CardDescription>
            {submissions.length} {language === "cs" ? "podání" : "submissions"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              {t.loading}
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12">
              <Send className="h-12 w-12 mx-auto mb-4 text-[var(--text-muted)]/30" />
              <h3 className="font-medium text-[var(--text-primary)]">{t.noSubmissions}</h3>
              <p className="text-sm text-[var(--text-muted)] mt-1">{t.noSubmissionsDesc}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.mission}</TableHead>
                  <TableHead>{t.status}</TableHead>
                  <TableHead>{t.submittedAt}</TableHead>
                  <TableHead>{t.expiresAt}</TableHead>
                  <TableHead>{t.updatedAt}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow key={submission.id} data-testid={`submission-row-${submission.id}`}>
                    <TableCell>
                      <div className="font-medium text-[var(--text-primary)]">
                        {submission.mission_name || submission.mission_id}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={submission.status} />
                    </TableCell>
                    <TableCell className="text-[var(--text-muted)]">
                      {formatDate(submission.submitted_at)}
                    </TableCell>
                    <TableCell className="text-[var(--text-muted)]">
                      {formatDate(submission.expires_at)}
                    </TableCell>
                    <TableCell className="text-[var(--text-muted)]">
                      {formatDate(submission.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetail(submission)}
                          className="h-8 px-2"
                          data-testid={`view-detail-${submission.id}`}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenStatusUpdate(submission)}
                          className="h-8 px-2"
                          data-testid={`update-status-${submission.id}`}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(submission)}
                          className="h-8 px-2"
                          data-testid={`edit-${submission.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {submission.status === "draft" && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setDeleteConfirm(submission)}
                            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-${submission.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedSubmission ? t.editSubmission : t.newSubmission}
            </DialogTitle>
            <DialogDescription>
              {language === "cs" 
                ? "Zadejte údaje o podání na ÚCL" 
                : "Enter CAA submission details"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t.mission}</Label>
              <Select 
                value={formData.mission_id} 
                onValueChange={(v) => setFormData({...formData, mission_id: v})}
                disabled={!!selectedSubmission}
              >
                <SelectTrigger data-testid="mission-select">
                  <SelectValue placeholder={t.selectMission} />
                </SelectTrigger>
                <SelectContent>
                  {missions.map((mission) => (
                    <SelectItem key={mission.id} value={mission.id}>
                      {mission.mission_id} - {mission.location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {!selectedSubmission && (
              <div className="space-y-2">
                <Label>{t.status}</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder={t.selectStatus} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {language === "cs" ? config.cs : config.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{t.expiresAt}</Label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                data-testid="expires-at-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t.notes}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder={t.statusNotePlaceholder}
                rows={3}
                data-testid="notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E3A5F] hover:bg-[#2a4a73]"
              data-testid="save-submission-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {saving ? t.saving : t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{t.updateStatus}</DialogTitle>
            <DialogDescription>
              {selectedSubmission?.mission_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t.status}</Label>
              <Select 
                value={statusUpdate.status} 
                onValueChange={(v) => setStatusUpdate({...statusUpdate, status: v})}
              >
                <SelectTrigger data-testid="new-status-select">
                  <SelectValue placeholder={t.selectStatus} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {language === "cs" ? config.cs : config.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t.statusNote}</Label>
              <Textarea
                value={statusUpdate.note}
                onChange={(e) => setStatusUpdate({...statusUpdate, note: e.target.value})}
                placeholder={t.statusNotePlaceholder}
                rows={3}
                data-testid="status-note-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleStatusUpdate}
              disabled={saving || !statusUpdate.status}
              className="bg-[#1E3A5F] hover:bg-[#2a4a73]"
              data-testid="confirm-status-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {saving ? t.saving : t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Detail Modal with Timeline */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {t.statusHistory}
            </DialogTitle>
            <DialogDescription>
              {selectedSubmission?.mission_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Current Status */}
            <div className="mb-6 p-4 bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-default)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[var(--text-muted)]">{t.status}</span>
                {selectedSubmission && <StatusBadge status={selectedSubmission.status} />}
              </div>
              {selectedSubmission?.notes && (
                <div className="mt-3 pt-3 border-t border-[var(--border-default)]">
                  <span className="text-sm text-[var(--text-muted)]">{t.notes}</span>
                  <p className="text-sm mt-1 text-[var(--text-primary)]">{selectedSubmission.notes}</p>
                </div>
              )}
              {selectedSubmission?.expires_at && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-[var(--text-muted)]" />
                  <span className="text-[var(--text-muted)]">{t.expiresAt}:</span>
                  <span className="text-[var(--text-primary)]">{formatDate(selectedSubmission.expires_at)}</span>
                </div>
              )}
            </div>
            
            {/* Timeline */}
            <div className="space-y-4">
              <h4 className="font-medium text-[var(--text-primary)]">{t.statusHistory}</h4>
              
              {!selectedSubmission?.history?.length ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-4">{t.noHistory}</p>
              ) : (
                <div className="relative pl-6 space-y-4">
                  {/* Timeline line */}
                  <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-[var(--border-default)]" />
                  
                  {selectedSubmission.history.slice().reverse().map((entry, idx) => {
                    const config = STATUS_CONFIG[entry.status] || STATUS_CONFIG.draft;
                    const Icon = config.icon;
                    
                    return (
                      <div key={idx} className="relative">
                        {/* Timeline dot */}
                        <div className={`absolute -left-4 w-4 h-4 rounded-full ${config.color} flex items-center justify-center`}>
                          <Icon className="h-2.5 w-2.5" />
                        </div>
                        
                        {/* Content */}
                        <div className="bg-[var(--bg-elevated)] rounded-lg p-3 border border-[var(--border-default)]">
                          <div className="flex items-center justify-between">
                            <StatusBadge status={entry.status} />
                            <span className="text-xs text-[var(--text-muted)]">
                              {formatDateTime(entry.changed_at)}
                            </span>
                          </div>
                          {entry.note && (
                            <p className="text-sm text-[var(--text-primary)] mt-2">{entry.note}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={() => {
                setShowDetailModal(false);
                handleOpenStatusUpdate(selectedSubmission);
              }}
              className="bg-[#1E3A5F] hover:bg-[#2a4a73]"
            >
              {t.updateStatus}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.deleteConfirmDesc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubmissionsPage;
